export type Uid<T extends string> = `${T}_${number}`;
export type Identity<T extends string> = T extends string ? { type: T; id: number; uid: Uid<T> } : never;
export type PartialIdentity<T extends string> = { type: T; id: number } | { uid: Uid<T> } | Identity<T>;

/**
 * Split a uid into its type and id
 * target_42 -> { type: "target", id: 42 }
 * @param uid The uid to split
 */
export function splitUid<T extends string = string>(uid: `${T}_${number}`): Identity<T> {
  const [type, id] = uid.split("_");
  return { type: type as T, id: parseInt(id), uid: uid as Uid<T> } as Identity<T>;
}

/**
 * Create a uid from a type and id
 * @param type The type of the uid
 * @param id The id of the uid (should be a number!)
 */
export function toUid<T extends string>(type: T, id: number | string) {
  return `${type}_${id}` as `${T}_${number}`;
}

/**
 * Convert an item with either a uid or type+id to a full id pack
 * @param item The item to convert
 * @returns The id pack
 */
export function normalizeIdentity<T extends string>(item: { uid: Uid<T> } | { type: T; id: number } | { type: T; id: number; uid: Uid<T> }): Identity<T> {
  if ("type" in item && "id" in item && "uid" in item) {
    return { type: item.type, id: item.id, uid: item.uid } as Identity<T>;
  } else if ("type" in item && "id" in item) {
    return { type: item.type, id: item.id, uid: toUid(item.type, item.id) } as Identity<T>;
  } else if ("uid" in item) {
    return splitUid(item.uid);
  }
  throw new Error("Invalid identity object");
}

export function getUid<T extends string>(item: { id: number; type: T; uid?: Uid<T> }): Uid<T> {
  if (item.uid) return item.uid;
  return toUid(item.type, item.id);
}

export function isUidOfType<T extends string>(type: T): (uid: Uid<string>) => uid is Uid<T>;
export function isUidOfType<T extends string>(uid: Uid<string>, type: T): uid is Uid<T>;
export function isUidOfType<T extends string>(uidOrType: Uid<string> | T, type?: T) {
  // If only one argument is given, return a function that checks the type
  if (arguments.length == 1) {
    return (uidToCheck: Uid<string>): uidToCheck is Uid<T> => {
      if (uidOrType == undefined) return false;
      return uidToCheck.startsWith(`${uidOrType}_`);
    }
  }
  if (uidOrType == undefined) return false;   
  return (uidOrType as Uid<string>).startsWith(`${type}_`);
}

export function isUidOfTypes<T extends string>(types: T[]): (uid: Uid<string>) => uid is Uid<T>;
export function isUidOfTypes<T extends string>(uid: Uid<string>, types: T[]): uid is Uid<T>;
export function isUidOfTypes<T extends string>(uidOrTypes: Uid<string> | T[], typesArg?: T[]) {
  // If only one argument is given, return a function that checks the type
  if (arguments.length == 1) {
    const types = uidOrTypes as T[];
    return (uidToCheck: Uid<string>): boolean => {
      return types.some((type) => uidToCheck.startsWith(`${type}_`));
    }
  }
  const uid = uidOrTypes as Uid<string>;
  const types = typesArg as T[];
  return types.some((type) => uid.startsWith(`${type}_`));
}
